/*
 * Black Label collection grid (2026-09)
 * - The section renders in-stock products first and sold-out products last (see main-collection-product-grid.liquid).
 * - Items beyond the first batch are rendered hidden; MORE reveals the next batch, and it also runs
 *   automatically when the button comes into view.
 * - For collections with more than 250 products, the next server page is fetched after everything is shown.
 * - Floating filter button (mobile) and back-to-top button.
 */
class BlackLabelCollection {
  constructor() {
    this.observer = null;
    document.addEventListener('click', (event) => this.onClick(event));
    this.init();

    // facets.js replaces the grid via the Section Rendering API when filters/sort change.
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      new MutationObserver(() => this.init()).observe(container, { childList: true });
    }
    this.initFloating();
    this.revealCurrentChip();
  }

  revealCurrentChip() {
    const current = document.querySelector('.bl-chips .bl-chip.is-current');
    const track = current?.closest('.bl-chips__track');
    if (!current || !track) return;
    const offset = current.offsetLeft - (track.clientWidth - current.offsetWidth) / 2;
    track.scrollLeft = Math.max(0, offset);
  }

  get wrapper() {
    return document.querySelector('#ProductGridContainer [data-bl-load-more]');
  }

  get grid() {
    return document.querySelector('#ProductGridContainer #product-grid');
  }

  init() {
    const wrapper = this.wrapper;
    this.updateCount();
    if (!wrapper) return;

    if (this.observer) this.observer.disconnect();
    const button = wrapper.querySelector('[data-bl-load-more-button]');
    if ('IntersectionObserver' in window && button) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.showMore();
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(button);
    }
  }

  onClick(event) {
    if (event.target.closest('[data-bl-load-more-button]')) {
      event.preventDefault();
      this.showMore();
      return;
    }
    if (event.target.closest('[data-bl-open-filters]')) {
      event.preventDefault();
      const summary = document.querySelector('.mobile-facets__wrapper summary');
      if (summary) summary.click();
      return;
    }
    if (event.target.closest('[data-bl-to-top]')) {
      event.preventDefault();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  hiddenItems() {
    const grid = this.grid;
    return grid ? Array.from(grid.querySelectorAll('[data-bl-item].bl-is-hidden')) : [];
  }

  showMore() {
    const wrapper = this.wrapper;
    if (!wrapper || wrapper.dataset.busy === 'true') return;
    const batch = parseInt(wrapper.dataset.batch, 10) || 16;
    const hidden = this.hiddenItems();

    if (hidden.length > 0) {
      let shown = 0;
      for (const item of hidden) {
        if (shown >= batch) break;
        item.classList.remove('bl-is-hidden');
        if (!item.classList.contains('bl-soldout-divider')) shown += 1;
      }
      this.afterReveal();
      return;
    }

    if (wrapper.dataset.nextUrl) {
      this.fetchNextPage(wrapper);
    }
  }

  afterReveal() {
    const wrapper = this.wrapper;
    this.updateCount();
    if (wrapper && this.hiddenItems().length === 0 && !wrapper.dataset.nextUrl) {
      if (this.observer) this.observer.disconnect();
      wrapper.querySelector('[data-bl-load-more-button]')?.remove();
    }
    document.dispatchEvent(new CustomEvent('blacklabel:products-loaded'));
  }

  updateCount() {
    const wrapper = this.wrapper;
    const grid = this.grid;
    if (!wrapper || !grid) return;
    const total = parseInt(wrapper.dataset.total, 10) || 0;
    const visible = grid.querySelectorAll('.grid__item[data-bl-item]:not(.bl-is-hidden)').length;
    const count = wrapper.querySelector('[data-bl-count]');
    if (count && total) count.textContent = `${Math.min(visible, total)} / ${total}件を表示`;
  }

  async fetchNextPage(wrapper) {
    const button = wrapper.querySelector('[data-bl-load-more-button]');
    wrapper.dataset.busy = 'true';
    if (button) {
      button.disabled = true;
      button.textContent = 'LOADING...';
    }
    try {
      const response = await fetch(wrapper.dataset.nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`読み込みに失敗しました: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = doc.querySelector('#ProductGridContainer #product-grid');
      if (!nextGrid) throw new Error('次の商品一覧が見つかりませんでした。');
      Array.from(nextGrid.children)
        .filter((item) => !item.classList.contains('bl-soldout-divider'))
        .forEach((item) => this.grid.appendChild(document.importNode(item, true)));
      const nextWrapper = doc.querySelector('#ProductGridContainer [data-bl-load-more]');
      if (nextWrapper?.dataset.nextUrl) wrapper.dataset.nextUrl = nextWrapper.dataset.nextUrl;
      else delete wrapper.dataset.nextUrl;
      if (button) {
        button.disabled = false;
        button.textContent = 'MORE';
      }
      wrapper.dataset.busy = 'false';
      this.showMore();
    } catch (error) {
      console.error('[Black Label MORE]', error);
      wrapper.dataset.busy = 'false';
      if (button) {
        button.disabled = false;
        button.textContent = 'RETRY';
      }
    }
  }

  initFloating() {
    const floating = document.querySelector('[data-bl-floating]');
    if (!floating) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const grid = document.querySelector('#ProductGridContainer');
      const footer = document.querySelector('.footer, footer');
      // Show once the filter bar above the grid has scrolled away.
      const pastFacets = grid ? grid.getBoundingClientRect().top < -120 : window.scrollY > 400;
      const nearFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight - 40 : false;
      floating.classList.toggle('is-visible', pastFacets && !nearFooter);
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }
}

if (!window.blackLabelCollectionInitialized) {
  window.blackLabelCollectionInitialized = true;
  new BlackLabelCollection();
}
